import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';
import { useUiStore } from '@/store/uiStore';

/**
 * LogoutModal Component
 *
 * Confirms user intent before ending the session.
 */
export const LogoutModal: React.FC = () => {
  const isOpen = useUiStore((state) => state.modals.logout);
  const closeModal = useUiStore((state) => state.closeModal);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    closeModal('logout');
    navigate('/login', { replace: true });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => closeModal('logout')}
      className='max-w-[388px] border-0'
      style={{
        background:
          'linear-gradient(180deg, rgba(255, 255, 255, 0) -1.92%, #A28DF6 140.84%), #FFFFFF',
      }}
    >
      <div className='flex flex-col items-center gap-6 pt-2'>
        {/* Text Content */}
        <div className='flex flex-col gap-6 text-center'>
          {/* Title */}
          <h2
            className={clsx(
              'font-proxima font-bold text-brand-800',
              // Size 24px, Line-height 25px
              'text-24 leading-25',
              'tracking-normal'
            )}
          >
            You’re about to log out of your account.
          </h2>

          {/* Description */}
          <p
            className={clsx(
              'font-sans font-normal',
              // #323232 maps to gray-600 in tailwind.config
              'text-gray-600',
              // Size 16px, Line-height 21px
              'text-16 leading-21',
              'tracking-normal',
              'mb-5'
            )}
          >
            Are you sure you want to continue?
          </p>
        </div>

        {/* Actions Row */}
        <div className='flex w-full items-center justify-between gap-4 px-2'>
          {/* Log Out Button (Left) */}
          <button
            onClick={handleLogout}
            className={clsx(
              // Font: Inter V, Semi Bold, 16px, 27px
              'font-sans text-16 font-semibold leading-27 tracking-normal',
              // Color: Black
              'text-black',
              'bg-transparent transition-opacity hover:opacity-70',
              'whitespace-nowrap'
            )}
          >
            Log out
          </button>

          {/* Stay Logged In Button (Right) */}
          <Button
            onClick={() => closeModal('logout')}
            variant='settings-modify'
            className={clsx('h-[40px] w-auto min-w-[140px]', 'text-16', 'text-brand-800')}
          >
            Stay logged in
          </Button>
        </div>
      </div>
    </Modal>
  );
};
